/**
 * Tech-stop / range planning + offered-fee math for long city-pairs.
 *
 * Uses the existing Hawker / type range constants (`CHARTER_RANGE_FACTOR`).
 * Tech-stop airports come only from an addon allowlist (Content.xml / imported
 * ICAOs). Empty or failed list → manual pick, never the full world DB.
 */

import { hydrateSceneryAirport, lookupAirport, resolveCoords } from "./airports";
import { type MoneyCatalogs } from "./catalogs";
import { parseContentXmlPackages, type ContentXmlPackage } from "./contentXml";
import { haversineNm } from "./geo";
import { eteMinutes, fuelPlan, validForAircraft } from "./generator";
import { needsTechStop, planningCruiseKt, practicalCharterRangeNm } from "./hawker800xp";
import { needsEmptyReturn, returnEmptyDestIcao, type CharterProposedLeg } from "./itinerary";
import {
  quoteCharter,
  type QuoteOpts,
} from "./ledger";
import { extractIcaosFromFolderName } from "./sceneryScan";
import { normalizeIcaoCode } from "./company";
import type {
  AircraftSpec,
  CharterRequest,
  Mission,
  RateTables,
  SceneryAirport,
} from "./types";

/** Occasional trickle long-haul — most auto RFQs stay single-leg. */
export const TECH_STOP_TRICKLE_RATE = 0.08;

/** Same-field follow-on waives empty-return uplift; within this NM reduces it. */
export const FOLLOW_ON_NEAR_NM = 80;

export const TECH_STOP_MIN_HOP_NM = 80;
export const TECH_STOP_MAX_DETOUR = 1.85;
export const FOLLOW_ON_NEAR_CREDIT = 0.5;

export type TechStopAllowlist = {
  /** Settings / career imported ICAOs. */
  imported: string[];
  /** Last Community / Content.xml scan hits. */
  inventory: string[];
};

export type TechStopPlan = {
  needed: boolean;
  pending: boolean;
  stopIcao?: string;
  stopName?: string;
  hops: CharterProposedLeg[];
  reason: string;
};

export type CharterFeeBreakdown = {
  baseCharterCad: number;
  repositionCad: number;
  followOnCreditCad: number;
  offeredCad: number;
  note: string;
};

export type FollowOnHint = {
  originIcao: string;
  destIcao?: string;
  id?: string;
  status?: string;
};

function icaoKey(value: string | undefined): string {
  return normalizeIcaoCode(value ?? "");
}

export function parseIcaoAllowlist(raw: string | string[] | undefined | null): string[] {
  const parts = Array.isArray(raw)
    ? raw
    : String(raw ?? "")
        .toUpperCase()
        .split(/[^A-Z0-9]+/);
  const out: string[] = [];
  const seen = new Set<string>();
  for (const part of parts) {
    const icao = normalizeIcaoCode(part);
    if (icao.length < 3 || icao.length > 4 || seen.has(icao)) continue;
    seen.add(icao);
    out.push(icao);
  }
  return out;
}

export function formatIcaoAllowlist(icaos: readonly string[]): string {
  return parseIcaoAllowlist([...icaos]).join(" ");
}

/** ICAOs mentioned in Content.xml package names / paths — not the world DB. */
export function icaosFromContentXmlText(xml: string, knownIcaos?: Iterable<string>): string[] {
  if (!xml?.trim()) return [];
  return icaosFromContentXmlPackages(parseContentXmlPackages(xml), knownIcaos);
}

export function icaosFromContentXmlPackages(
  packages: ContentXmlPackage[],
  knownIcaos?: Iterable<string>,
): string[] {
  const hits = new Set<string>();
  for (const pkg of packages) {
    if (!pkg.enabled) continue;
    for (const token of extractIcaosFromFolderName(pkg.name, knownIcaos)) hits.add(token);
    if (pkg.path) {
      for (const token of extractIcaosFromFolderName(pkg.path, knownIcaos)) hits.add(token);
    }
  }
  return [...hits].sort();
}

/**
 * Prefer the imported Settings list. Fall back to scan inventory.
 * Never invent stock catalog airports when both are empty.
 */
export function effectiveTechStopAllowlist(lists: TechStopAllowlist): string[] {
  const imported = parseIcaoAllowlist(lists.imported);
  if (imported.length) return imported;
  return parseIcaoAllowlist(lists.inventory);
}

export function filterTechStopCandidates(
  candidates: ReadonlyArray<SceneryAirport | { icao: string }>,
  allowlist: readonly string[],
): SceneryAirport[] {
  const allowed = new Set(parseIcaoAllowlist([...allowlist]));
  if (!allowed.size) return [];
  const out: SceneryAirport[] = [];
  const seen = new Set<string>();
  for (const raw of candidates) {
    const icao = icaoKey(raw.icao);
    if (!icao || !allowed.has(icao) || seen.has(icao)) continue;
    const row = hydrateSceneryAirport(icao, [raw as SceneryAirport]) ?? hydrateSceneryAirport(icao, []);
    if (!row) continue;
    seen.add(icao);
    out.push(row);
  }
  return out;
}

export function hopDistanceNm(
  originIcao: string,
  destIcao: string,
  scenery: SceneryAirport[],
): number | null {
  const oc = resolveCoords(icaoKey(originIcao), scenery);
  const dc = resolveCoords(icaoKey(destIcao), scenery);
  if (!oc || !dc) return null;
  return haversineNm(oc.lat, oc.lon, dc.lat, dc.lon);
}

function hopFits(
  spec: AircraftSpec,
  originIcao: string,
  destIcao: string,
  dest: SceneryAirport,
  scenery: SceneryAirport[],
): boolean {
  const nm = hopDistanceNm(originIcao, destIcao, scenery);
  if (nm == null) return false;
  if (nm < TECH_STOP_MIN_HOP_NM) return false;
  return validForAircraft(spec, dest.runwayFt, nm, false);
}

export function pickTechStopAirport(input: {
  origin: SceneryAirport;
  dest: SceneryAirport;
  spec: AircraftSpec;
  scenery: SceneryAirport[];
  allowlist: readonly string[];
  manualIcao?: string;
}): SceneryAirport | null {
  const originIcao = icaoKey(input.origin.icao);
  const destIcao = icaoKey(input.dest.icao);
  const manual = icaoKey(input.manualIcao);
  if (manual && manual !== originIcao && manual !== destIcao) {
    const via =
      hydrateSceneryAirport(manual, input.scenery) ??
      hydrateSceneryAirport(manual, []) ??
      (lookupAirport(manual) ? hydrateSceneryAirport(manual, []) : null);
    if (via && hopFits(input.spec, originIcao, via.icao, via, [via, ...input.scenery]) && hopFits(input.spec, via.icao, destIcao, input.dest, [via, ...input.scenery])) {
      return via;
    }
    if (via) return via;
  }

  const allowlist = parseIcaoAllowlist([...input.allowlist]);
  if (!allowlist.length) return null;

  const oc = resolveCoords(originIcao, [input.origin, ...input.scenery]);
  const dc = resolveCoords(destIcao, [input.dest, ...input.scenery]);
  if (!oc || !dc) return null;
  const direct = haversineNm(oc.lat, oc.lon, dc.lat, dc.lon);

  const pool = filterTechStopCandidates(
    [
      ...input.scenery,
      ...allowlist.map((icao) => hydrateSceneryAirport(icao, input.scenery) ?? hydrateSceneryAirport(icao, [])).filter(Boolean),
    ] as SceneryAirport[],
    allowlist,
  );

  let best: { via: SceneryAirport; score: number } | null = null;
  for (const via of pool) {
    const icao = icaoKey(via.icao);
    if (icao === originIcao || icao === destIcao) continue;
    const vc = resolveCoords(icao, [via, ...input.scenery]);
    if (!vc) continue;
    const a = haversineNm(oc.lat, oc.lon, vc.lat, vc.lon);
    const b = haversineNm(vc.lat, vc.lon, dc.lat, dc.lon);
    if (a < TECH_STOP_MIN_HOP_NM || b < TECH_STOP_MIN_HOP_NM) continue;
    if (a + b > direct * TECH_STOP_MAX_DETOUR) continue;
    if (!hopFits(input.spec, originIcao, icao, via, [via, ...input.scenery])) continue;
    if (!hopFits(input.spec, icao, destIcao, input.dest, [via, ...input.scenery])) continue;
    const detour = a + b - direct;
    const midPenalty = Math.abs(a - b);
    const score = detour + midPenalty * 0.15;
    if (!best || score < best.score) best = { via, score };
  }
  return best?.via ?? null;
}

export function planTechStopRoute(input: {
  origin: SceneryAirport;
  dest: SceneryAirport;
  spec: AircraftSpec;
  paxCount: number;
  scenery: SceneryAirport[];
  allowlist: readonly string[];
  homeIcao?: string;
  manualIcao?: string;
  mxPositioning?: boolean;
}): TechStopPlan {
  const scenery = [input.origin, input.dest, ...input.scenery];
  const direct = hopDistanceNm(input.origin.icao, input.dest.icao, scenery);
  if (direct == null) {
    return { needed: false, pending: false, hops: [], reason: "Missing coordinates." };
  }
  if (!needsTechStop(direct, input.spec)) {
    return { needed: false, pending: false, hops: [], reason: "Direct city-pair is inside practical range." };
  }

  const via = pickTechStopAirport({
    origin: input.origin,
    dest: input.dest,
    spec: input.spec,
    scenery,
    allowlist: input.allowlist,
    manualIcao: input.manualIcao,
  });
  const home = returnEmptyDestIcao(input.homeIcao, input.origin.icao);
  const empty = needsEmptyReturn(input.dest.icao, home, { mxPositioning: input.mxPositioning });

  if (!via) {
    const hops: CharterProposedLeg[] = [
      {
        originIcao: input.origin.icao,
        destIcao: "TBD",
        originName: input.origin.name,
        destName: "Tech stop (pick me)",
        paxCount: input.paxCount,
        techStop: true,
        notes: "Tech / refuel stop required — pick an addon or manual ICAO. Not a pax revenue sector.",
      },
      {
        originIcao: "TBD",
        destIcao: input.dest.icao,
        originName: "Tech stop",
        destName: input.dest.name,
        paxCount: input.paxCount,
        notes: "Continue after the tech stop.",
      },
    ];
    if (empty) {
      hops.push({
        originIcao: input.dest.icao,
        destIcao: home,
        destName: "Home",
        paxCount: 0,
        empty: true,
        notes: "Empty return ferry — no charter revenue.",
      });
    }
    return {
      needed: true,
      pending: true,
      hops,
      reason: "Out of practical range. No allowlisted tech-stop ICAO — pick one manually.",
    };
  }

  const hops: CharterProposedLeg[] = [
    {
      originIcao: input.origin.icao,
      destIcao: via.icao,
      originName: input.origin.name,
      destName: via.name,
      paxCount: input.paxCount,
      techStop: true,
      notes: `Tech / refuel at ${via.icao} — not a pax revenue sector.`,
    },
    {
      originIcao: via.icao,
      destIcao: input.dest.icao,
      originName: via.name,
      destName: input.dest.name,
      paxCount: input.paxCount,
      notes: `Continue ${via.icao}–${input.dest.icao}.`,
    },
  ];
  if (empty) {
    hops.push({
      originIcao: input.dest.icao,
      destIcao: home,
      destName: "Home",
      paxCount: 0,
      empty: true,
      notes: "Empty return ferry — no charter revenue.",
    });
  }
  return {
    needed: true,
    pending: false,
    stopIcao: via.icao,
    stopName: via.name,
    hops,
    reason: `Direct ${Math.round(direct)} NM exceeds practical range ${Math.round(practicalCharterRangeNm(input.spec))} NM. Tech stop ${via.icao}.`,
  };
}

export function payingOrTechStopLegs(
  legs: ReadonlyArray<CharterProposedLeg> | undefined,
): CharterProposedLeg[] {
  return (legs ?? []).filter((leg) => !leg.empty);
}

export function originNearDest(
  originIcao: string,
  destIcao: string,
  scenery: SceneryAirport[],
  limitNm = FOLLOW_ON_NEAR_NM,
): boolean {
  const a = icaoKey(originIcao);
  const b = icaoKey(destIcao);
  if (!a || !b) return false;
  if (a === b) return true;
  const nm = hopDistanceNm(a, b, scenery);
  return nm != null && nm <= limitNm;
}

export function findFollowOnNearDest(input: {
  destIcao: string;
  scenery: SceneryAirport[];
  inbox?: ReadonlyArray<Pick<CharterRequest, "id" | "status" | "originIcao" | "destIcao" | "kind" | "itineraryKind">>;
  missions?: ReadonlyArray<
    Pick<Mission, "originIcao" | "destIcao"> &
      Partial<Pick<Mission, "id" | "status" | "flightType" | "inboxRequestId">>
  >;
  skipRequestId?: string;
  limitNm?: number;
}): FollowOnHint | null {
  const dest = icaoKey(input.destIcao);
  if (!dest) return null;
  const limit = input.limitNm ?? FOLLOW_ON_NEAR_NM;
  for (const row of input.inbox ?? []) {
    if (row.id === input.skipRequestId) continue;
    if (row.kind && row.kind !== "charter") continue;
    if (row.status === "declined") continue;
    if (row.itineraryKind === "backhaul") continue;
    if (originNearDest(row.originIcao, dest, input.scenery, limit)) {
      return { originIcao: row.originIcao, destIcao: row.destIcao, id: row.id, status: row.status };
    }
  }
  for (const m of input.missions ?? []) {
    if (m.status === "completed" || m.status === "cancelled") continue;
    if (m.flightType === "ferry") continue;
    if (m.inboxRequestId && m.inboxRequestId === input.skipRequestId) continue;
    if (originNearDest(m.originIcao, dest, input.scenery, limit)) {
      return { originIcao: m.originIcao, destIcao: m.destIcao, id: m.id, status: m.status };
    }
  }
  return null;
}

function virtualFerryMission(
  spec: AircraftSpec,
  fromIcao: string,
  toIcao: string,
  scenery: SceneryAirport[],
  now: Date,
): Mission | null {
  const origin = hydrateSceneryAirport(fromIcao, scenery) ?? hydrateSceneryAirport(fromIcao, []);
  const dest = hydrateSceneryAirport(toIcao, scenery) ?? hydrateSceneryAirport(toIcao, []);
  if (!origin || !dest) return null;
  const nm = hopDistanceNm(fromIcao, toIcao, [origin, dest, ...scenery]);
  if (nm == null || nm < 8) return null;
  const eteMin = eteMinutes(nm, planningCruiseKt(spec, nm));
  const fuel = fuelPlan(spec, eteMin);
  return {
    id: `repo-${fromIcao}-${toIcao}`,
    createdAt: now.toISOString(),
    callsign: spec.registration,
    aircraftId: spec.id,
    aircraftName: spec.name,
    registration: spec.registration,
    flightType: "ferry",
    originIcao: origin.icao,
    destIcao: dest.icao,
    originName: origin.name,
    destName: dest.name,
    originCity: origin.city,
    destCity: dest.city,
    distanceNm: Math.round(nm),
    eteMin,
    fuelGal: fuel.fuelGal,
    fuelLbs: fuel.fuelLbs,
    trackTrue: 0,
    paxCount: 0,
    paxNames: [],
    cargoLb: 0,
    scenario: "Empty return / reposition estimate",
    purpose: "Empty return",
    departureIso: now.toISOString(),
    weatherNote: "",
    originMetar: "",
    destMetar: "",
    status: "pending",
    isMaintenance: false,
    originRunwayFt: origin.runwayFt,
    destRunwayFt: dest.runwayFt,
    legs: [],
    source: "inbox",
  };
}

/** Company cost of dest→home empty return — baked into the offered charter fee. */
export function estimateRepositionCad(input: {
  spec: AircraftSpec;
  fromIcao: string;
  toIcao: string;
  scenery: SceneryAirport[];
  rates: RateTables;
  catalogs?: MoneyCatalogs;
  now?: Date;
}): number {
  const now = input.now ?? new Date();
  const ferry = virtualFerryMission(input.spec, input.fromIcao, input.toIcao, input.scenery, now);
  if (!ferry) return 0;
  const quote = quoteCharter(ferry, input.rates, undefined, {
    catalogs: input.catalogs,
    aircraft: input.spec,
    parkingDays: 0,
  } satisfies QuoteOpts);
  return Math.round((quote.fuelCad + quote.landingCad + quote.handlingCad) * 100) / 100;
}

export function quoteFeeBreakdown(input: {
  baseCharterCad: number;
  spec: AircraftSpec;
  destIcao: string;
  homeIcao?: string;
  originIcao?: string;
  scenery: SceneryAirport[];
  rates: RateTables;
  catalogs?: MoneyCatalogs;
  returnEmpty?: boolean;
  mxPositioning?: boolean;
  itineraryKind?: CharterRequest["itineraryKind"];
  followOn?: FollowOnHint | null;
  now?: Date;
}): CharterFeeBreakdown {
  const home = returnEmptyDestIcao(input.homeIcao, input.originIcao);
  const needsRepo =
    Boolean(input.returnEmpty) &&
    !input.mxPositioning &&
    input.itineraryKind !== "backhaul" &&
    icaoKey(input.destIcao) !== home;
  const repositionCad = needsRepo
    ? estimateRepositionCad({
        spec: input.spec,
        fromIcao: input.destIcao,
        toIcao: home,
        scenery: input.scenery,
        rates: input.rates,
        catalogs: input.catalogs,
        now: input.now,
      })
    : 0;
  let followOnCreditCad = 0;
  if (repositionCad > 0 && input.followOn) {
    const same = icaoKey(input.followOn.originIcao) === icaoKey(input.destIcao);
    followOnCreditCad = Math.round(repositionCad * (same ? 1 : FOLLOW_ON_NEAR_CREDIT) * 100) / 100;
  }
  const offeredCad = Math.round((input.baseCharterCad + repositionCad - followOnCreditCad) * 100) / 100;
  const bits = ["Desk rate card — not a Grok figure"];
  if (repositionCad > 0) bits.push("includes empty-return / reposition");
  if (followOnCreditCad > 0) bits.push("follow-on near dest credited");
  return {
    baseCharterCad: Math.round(input.baseCharterCad * 100) / 100,
    repositionCad,
    followOnCreditCad,
    offeredCad,
    note: bits.join(" · "),
  };
}

export function applyTechStopPlanToRequest(
  row: CharterRequest,
  plan: TechStopPlan,
): CharterRequest {
  if (!plan.needed) return row;
  const lastPaying = payingOrTechStopLegs(plan.hops).at(-1);
  const note = plan.pending
    ? "Tech stop required (TBD — pick an addon / manual ICAO). Tech-stop hops are not pax revenue."
    : `Tech stop ${plan.stopIcao}. Refuel hop is not a pax revenue sector.`;
  return {
    ...row,
    destIcao: lastPaying && lastPaying.destIcao !== "TBD" ? lastPaying.destIcao : row.destIcao,
    destName: lastPaying?.destName && lastPaying.destIcao !== "TBD" ? lastPaying.destName : row.destName,
    itineraryKind: "tech_stop",
    techStopIcao: plan.stopIcao,
    techStopPending: plan.pending,
    proposedLegs: plan.hops,
    returnEmpty: plan.hops.some((l) => l.empty),
    specialRequests: [row.specialRequests, note].filter(Boolean).join(" "),
    emailBody: row.emailBody?.includes(note) ? row.emailBody : `${row.emailBody?.trimEnd() ?? ""}\n\n${note}`.trim(),
    warning: plan.pending ? plan.reason : row.warning,
  };
}

export function applyManualTechStop(input: {
  request: CharterRequest;
  stopIcao: string;
  spec: AircraftSpec;
  scenery: SceneryAirport[];
  allowlist?: readonly string[];
  homeIcao?: string;
}): CharterRequest | { ok: false; error: string } {
  const stop = icaoKey(input.stopIcao);
  if (stop.length < 3) return { ok: false, error: "Enter a 4-letter tech-stop ICAO." };
  const origin = hydrateSceneryAirport(input.request.originIcao, input.scenery);
  const dest = hydrateSceneryAirport(input.request.destIcao, input.scenery);
  if (!origin || !dest) return { ok: false, error: "Could not resolve origin or destination." };
  const via = hydrateSceneryAirport(stop, input.scenery) ?? hydrateSceneryAirport(stop, []);
  if (!via) return { ok: false, error: `Unknown tech-stop ICAO ${stop}.` };
  const plan = planTechStopRoute({
    origin,
    dest,
    spec: input.spec,
    paxCount: input.request.paxCount,
    scenery: input.scenery,
    allowlist: input.allowlist?.length ? input.allowlist : [stop],
    homeIcao: input.homeIcao,
    manualIcao: stop,
    mxPositioning: input.request.mxPositioning,
  });
  if (!plan.needed) {
    return {
      ...input.request,
      techStopIcao: undefined,
      techStopPending: false,
      warning: undefined,
    };
  }
  if (plan.pending && !plan.stopIcao) {
    return { ok: false, error: `${stop} does not split this city-pair inside practical range.` };
  }
  return applyTechStopPlanToRequest(input.request, { ...plan, pending: false, stopIcao: plan.stopIcao ?? stop });
}

export function requestNeedsTechStopPick(row: Pick<CharterRequest, "itineraryKind" | "techStopPending" | "techStopIcao">): boolean {
  return row.itineraryKind === "tech_stop" && (Boolean(row.techStopPending) || !row.techStopIcao);
}

export { needsTechStop, practicalCharterRangeNm };
